import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  saveCustomerProfile,
  useAuthToken,
  useCustomerProfile,
  type CustomerProfile,
} from "@/lib/hooks";
import { fmtDate } from "@/lib/format";

const CREDENTIALING_OPTIONS: { value: NonNullable<CustomerProfile["credentialing_status"]>; label: string }[] = [
  { value: "not_started",    label: "Not started" },
  { value: "in_progress",    label: "In progress" },
  { value: "complete",       label: "Complete" },
  { value: "refresh_needed", label: "Refresh needed" },
];

const EMR_OPTIONS = ["Epic", "Cerner", "Meditech", "Other"];

const empty: CustomerProfile = {
  emr_system: null,
  epic_integrated: null,
  epic_integration_date: null,
  credentialing_status: null,
  credentialed_nurse_count: null,
  last_inservice_date: null,
  next_inservice_date: null,
  notes: null,
};

export default function ProfileEditor({ customerId }: { customerId: string }) {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { data, isLoading } = useCustomerProfile(customerId);

  const [draft, setDraft] = useState<CustomerProfile>(empty);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setDraft(data.profile);
      setIsActive(data.is_active);
    }
  }, [data]);

  const set = <K extends keyof CustomerProfile>(k: K, v: CustomerProfile[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  async function onSave() {
    setSaving(true);
    setSavedMessage(null);
    try {
      const res = await saveCustomerProfile(token, customerId, { ...draft, is_active: isActive });
      setDraft(res.profile);
      setIsActive(res.is_active);
      setSavedMessage(`Saved at ${new Date().toLocaleTimeString()}`);
      // Invalidate everything that hides/shows departed customers + reflects profile state
      queryClient.invalidateQueries({ queryKey: ["action-items"] });
      queryClient.invalidateQueries({ queryKey: ["customer-profile", customerId] });
      queryClient.invalidateQueries({ queryKey: ["customer-weekly", customerId] });
      queryClient.invalidateQueries({ queryKey: ["segmentation"] });
      queryClient.invalidateQueries({ queryKey: ["customers-yearly"] });
      queryClient.invalidateQueries({ queryKey: ["silent-customers"] });
      queryClient.invalidateQueries({ queryKey: ["kpis"] });
    } catch (e) {
      setSavedMessage(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) {
    return <div className="card p-5 text-sm text-ink-500">Loading profile…</div>;
  }

  return (
    <section className="card p-5">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-sm font-medium text-ink-900">Customer profile</h2>
        {data?.updated_at && (
          <span className="text-xs text-ink-500">
            Last edited {fmtDate(data.updated_at)} by {data.updated_by ?? "—"}
          </span>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-ink-200 bg-ink-50/50 px-3 py-2">
        <div>
          <span className="stat-label">Account status</span>
          <p className="mt-0.5 text-xs text-ink-500">
            Departed customers are hidden from all dashboard views and reports.
          </p>
        </div>
        <select
          value={isActive ? "active" : "departed"}
          onChange={(e) => setIsActive(e.target.value === "active")}
          className="input max-w-[12rem]"
        >
          <option value="active">Active</option>
          <option value="departed">Departed (left us)</option>
        </select>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="EMR system">
          <select
            value={draft.emr_system ?? ""}
            onChange={(e) => set("emr_system", e.target.value || null)}
            className="input"
          >
            <option value="">—</option>
            {EMR_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </Field>

        <Field label="Accuryn integrated with EMR?">
          <select
            value={draft.epic_integrated === null ? "" : draft.epic_integrated ? "yes" : "no"}
            onChange={(e) =>
              set("epic_integrated", e.target.value === "" ? null : e.target.value === "yes")
            }
            className="input"
          >
            <option value="">—</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </Field>

        <Field label="Integration date">
          <input
            type="date"
            value={draft.epic_integration_date ?? ""}
            onChange={(e) => set("epic_integration_date", e.target.value || null)}
            className="input"
          />
        </Field>

        <Field label="Credentialing status">
          <select
            value={draft.credentialing_status ?? ""}
            onChange={(e) =>
              set("credentialing_status", (e.target.value || null) as CustomerProfile["credentialing_status"])
            }
            className="input"
          >
            <option value="">—</option>
            {CREDENTIALING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>

        <Field label="# credentialed nurses">
          <input
            type="number"
            min={0}
            value={draft.credentialed_nurse_count ?? ""}
            onChange={(e) =>
              set("credentialed_nurse_count", e.target.value === "" ? null : Number(e.target.value))
            }
            className="input"
          />
        </Field>

        <Field label="Last in-service">
          <input
            type="date"
            value={draft.last_inservice_date ?? ""}
            onChange={(e) => set("last_inservice_date", e.target.value || null)}
            className="input"
          />
        </Field>

        <Field label="Next in-service">
          <input
            type="date"
            value={draft.next_inservice_date ?? ""}
            onChange={(e) => set("next_inservice_date", e.target.value || null)}
            className="input"
          />
        </Field>

        <div className="sm:col-span-2">
          <Field label="Notes">
            <textarea
              rows={3}
              value={draft.notes ?? ""}
              onChange={(e) => set("notes", e.target.value || null)}
              className="input"
              placeholder="Staffing changes, ordering patterns, key contacts, etc."
            />
          </Field>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-3">
        {savedMessage && <span className="text-xs text-ink-500">{savedMessage}</span>}
        <button type="button" className="btn-primary text-sm" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save profile"}
        </button>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="stat-label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
